import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuthStore } from './stores/auth.store';
import { supabase } from './lib/supabase';
import { StaffRole } from '@bisa/shared';
import { PageSpinner } from './components/ui/Spinner';
import { AppShell } from './components/layout/AppShell';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { FacilitiesPage } from './pages/super-admin/FacilitiesPage';
import { GlobalParamsPage } from './pages/super-admin/GlobalParamsPage';
import { LookupsPage } from './pages/super-admin/LookupsPage';
import { DepartmentsPage } from './pages/facility-admin/DepartmentsPage';
import { ServicesPage } from './pages/facility-admin/ServicesPage';
import { StaffPage } from './pages/facility-admin/StaffPage';
import { TenantParamsPage } from './pages/facility-admin/TenantParamsPage';
import { QueuePage } from './pages/QueuePage';
import { ReportsPage } from './pages/ReportsPage';

function AuthHandler() {
  const navigate = useNavigate();
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        navigate('/reset-password', { replace: true });
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate]);
  return null;
}

function RootRedirect() {
  const { session, kind, staff, loading } = useAuthStore();
  if (loading) return <PageSpinner />;
  if (!session) return <Navigate to="/login" replace />;
  if (kind === 'super_admin') return <Navigate to="/admin/facilities" replace />;
  if (staff?.role === StaffRole.ADMIN) return <Navigate to="/facility/departments" replace />;
  return <Navigate to="/queue" replace />;
}

export default function App() {
  const init = useAuthStore((s) => s.init);

  useEffect(() => {
    init();
  }, [init]);

  return (
    <BrowserRouter>
      <AuthHandler />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        <Route
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route index element={<RootRedirect />} />

          {/* Super Admin */}
          <Route
            path="/admin/facilities"
            element={
              <ProtectedRoute requireKind="super_admin">
                <FacilitiesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/params"
            element={
              <ProtectedRoute requireKind="super_admin">
                <GlobalParamsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/lookups"
            element={
              <ProtectedRoute requireKind="super_admin">
                <LookupsPage />
              </ProtectedRoute>
            }
          />

          {/* Facility Admin (MANAGEMENT role) */}
          <Route
            path="/facility/departments"
            element={
              <ProtectedRoute requireRole={[StaffRole.ADMIN]}>
                <DepartmentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/facility/services"
            element={
              <ProtectedRoute requireRole={[StaffRole.ADMIN]}>
                <ServicesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/facility/staff"
            element={
              <ProtectedRoute requireRole={[StaffRole.ADMIN]}>
                <StaffPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/facility/params"
            element={
              <ProtectedRoute requireRole={[StaffRole.ADMIN]}>
                <TenantParamsPage />
              </ProtectedRoute>
            }
          />

          {/* Queue — all staff roles */}
          <Route
            path="/queue"
            element={
              <ProtectedRoute requireRole={[StaffRole.FRONT_DESK, StaffRole.PROVIDER, StaffRole.ADMIN]}>
                <QueuePage />
              </ProtectedRoute>
            }
          />

          {/* Admin reports */}
          <Route
            path="/reports"
            element={
              <ProtectedRoute requireRole={[StaffRole.ADMIN]}>
                <ReportsPage />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
